/**
 * Mixpanel Analytics Configuration
 *
 * This module configures Mixpanel for product analytics and user tracking.
 * It integrates with @bvengineering/cas-analytics-events for shared event types
 * across mobile and web platforms.
 *
 * Mixpanel is DISABLED by default in ALL environments. To enable it, set
 * VITE_MIXPANEL_ENABLED=true in your environment variables. This prevents
 * unnecessary event traffic when the codebase is used by other teams.
 */

import {
  EventNames,
  EventProperties,
  MixpanelEventManager,
} from '@bvengineering/cas-analytics-events';
import mixpanel from 'mixpanel-browser';
import { ENV, isDev } from './env';

// Re-export types from CAS analytics for convenience
export type { EventNames, EventProperties } from '@bvengineering/cas-analytics-events';

// Track initialization state
let isInitialized = false;

/**
 * Check if Mixpanel is initialized
 */
const isMixpanelInitialized = (): boolean => isInitialized;

/**
 * Initialize Mixpanel analytics
 */
export const initializeMixpanel = (): void => {
  const token = import.meta.env.VITE_MIXPANEL_TOKEN || '';

  // Mixpanel is disabled by default in ALL environments until first prod release.
  // Set VITE_MIXPANEL_ENABLED=true to explicitly enable event tracking.
  const enabled = import.meta.env.VITE_MIXPANEL_ENABLED === 'true';

  if (!enabled) {
    if (isDev()) {
      console.log('[Mixpanel] Disabled. Set VITE_MIXPANEL_ENABLED=true to enable.');
    }
    return;
  }

  if (!token) {
    console.warn('[Mixpanel] Token not configured. Set VITE_MIXPANEL_TOKEN environment variable.');
    return;
  }

  mixpanel.init(token, {
    debug: isDev(),
    track_pageview: true,
    persistence: 'localStorage',
    ip: false,
  });

  // Register super properties (included with every event)
  // 'source' distinguishes this app from mobile app ('app') and other web apps
  mixpanel.register({
    source: 'creators_web',
    app_version: import.meta.env.VITE_APP_VERSION || '0.0.0',
  });

  isInitialized = true;
  console.log(`[Mixpanel] Initialized for ${ENV.env} environment`);
};

/**
 * Identify user for Mixpanel tracking
 * Call this after user authentication
 */
export const identifyUser = (
  userId: string,
  traits?: {
    $first_name?: string;
    $last_name?: string;
    $email?: string;
    gender?: string;
    selected_language?: string;
    [key: string]: unknown;
  }
): void => {
  if (!isMixpanelInitialized()) return;

  mixpanel.identify(userId);

  if (traits) {
    // Set user properties
    mixpanel.people.set({
      ...traits,
      user_id: userId,
    });
  }
};

/**
 * Reset user identity (call on logout)
 */
export const resetUser = (): void => {
  if (!isMixpanelInitialized()) return;

  mixpanel.reset();
};

/**
 * Track a typed event using CAS shared event types
 * This is the primary method for tracking events with type safety
 */
export const trackEvent = <E extends EventNames>(
  eventName: E,
  properties?: EventProperties<E>
): void => {
  if (!isMixpanelInitialized()) return;

  mixpanel.track(eventName, {
    ...properties,
    environment: ENV.env,
  });
};

/**
 * Track a custom event (for events not in CAS shared types)
 * Use trackEvent for typed events when possible
 */
export const trackCustomEvent = (eventName: string, properties?: Record<string, unknown>): void => {
  if (!isMixpanelInitialized()) return;

  mixpanel.track(eventName, {
    ...properties,
    environment: ENV.env,
  });
};

/**
 * Track page view
 */
export const trackPageView = (pageName: string, properties?: Record<string, unknown>): void => {
  trackCustomEvent('Page View', {
    page: pageName,
    ...properties,
  });
};

/**
 * Set super properties (included with every event)
 */
export const setSuperProperties = (properties: Record<string, unknown>): void => {
  if (!isMixpanelInitialized()) return;

  mixpanel.register(properties);
};

/**
 * MixpanelManager class implementing MixpanelEventManager interface
 * This provides compatibility with the mobile app's analytics architecture
 */
export class MixpanelManager implements MixpanelEventManager {
  private initialized = false;

  constructor() {
    // Check if already initialized
    this.initialized = isMixpanelInitialized();
  }

  /**
   * Initialize Mixpanel (if not already initialized)
   */
  init(): void {
    if (!this.initialized) {
      initializeMixpanel();
      this.initialized = isMixpanelInitialized();
    }
  }

  /**
   * Track an event with typed properties
   * Implements MixpanelEventManager interface
   */
  event<E extends EventNames>(eventName: E, properties?: EventProperties<E>): void {
    trackEvent(eventName, properties);
  }

  /**
   * Identify user
   */
  identifyUser(
    userId: string,
    traits?: {
      $first_name?: string;
      $last_name?: string;
      $email?: string;
      gender?: string;
      selected_language?: string;
    }
  ): void {
    identifyUser(userId, traits);
  }

  /**
   * Reset user identity
   */
  reset(): void {
    resetUser();
  }

  /**
   * Set super properties
   */
  registerSuperProperties(properties: Record<string, unknown>): void {
    setSuperProperties(properties);
  }

  /**
   * Flush events (browser SDK handles this automatically)
   */
  flush(): void {
    // Browser SDK auto-flushes, but we can force it if needed
    if (isMixpanelInitialized()) {
      // mixpanel-browser doesn't have a flush method like react-native
      // Events are sent immediately or batched automatically
    }
  }
}

// Singleton instance for convenience
let mixpanelManagerInstance: MixpanelManager | null = null;

export const getMixpanelManager = (): MixpanelManager => {
  if (!mixpanelManagerInstance) {
    mixpanelManagerInstance = new MixpanelManager();
  }
  return mixpanelManagerInstance;
};
